import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Tag, KeyRound, Boxes, PenLine, MapPin, ArrowRight, MessageCircle } from "lucide-react";
import { SITE, IMAGES } from "@/config/site";
import { scrollToFormField } from "@/lib/scroll";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PERKS = [
  { icon: ShieldCheck, label: "Seguridad y videovigilancia", hot: false },
  { icon: Tag, label: "Descuento de apertura", hot: true },
  { icon: KeyRound, label: "Acceso cómodo y flexible", hot: false },
  { icon: Boxes, label: "Diferentes tamaños", hot: false },
  { icon: PenLine, label: "Contratación sencilla", hot: false },
  { icon: MapPin, label: "Calle Perú 2 · Guadalajara", hot: false },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section id="inicio" data-testid="hero-section" className="relative min-h-[100svh] flex flex-col overflow-hidden bg-brand-ink">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-56">
        <img
          src={IMAGES.hero}
          alt="Pasillo de trasteros modernos con puertas amarillas en Guadalajara"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/75 to-brand-ink/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-transparent to-brand-ink/40" />

      <div className="relative flex-1 flex items-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 font-mono text-[11px] sm:text-xs font-bold tracking-[0.3em] uppercase text-brand-yellow border border-brand-yellow/40 rounded-full px-4 py-2 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow animate-pulse" />
            Próxima apertura · Calle Perú 2, Guadalajara
          </motion.div>

          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.04] tracking-tight">
            <MaskedLine delay={0.25}>El espacio que te falta,</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-brand-yellow">sin salir de Guadalajara</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed"
          >
            Trasteros seguros y de fácil acceso para particulares, autónomos y empresas.
            Próxima apertura en Calle Perú 2.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <motion.button
              data-testid="hero-primary-cta"
              onClick={() => scrollToFormField()}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-ink font-display font-extrabold tracking-wide px-8 py-4 rounded-full shadow-[0_10px_35px_rgba(255,230,0,0.35)] hover:bg-brand-yellowhover transition-colors duration-200"
            >
              PRERRESERVAR MI TRASTERO
              <ArrowRight size={18} strokeWidth={2.6} />
            </motion.button>
            <motion.a
              data-testid="hero-whatsapp-cta"
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/35 text-white font-display font-bold tracking-wide px-8 py-4 rounded-full hover:bg-white/10 hover:border-white/60 transition-[background-color,border-color] duration-200"
            >
              <MessageCircle size={18} />
              HABLAR POR WHATSAPP
            </motion.a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-4 font-mono text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase text-white/40"
          >
            Sin pago y sin compromiso hasta confirmar apertura y unidad
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.05 }}
        className="relative border-t border-white/15 bg-brand-ink/60 backdrop-blur-md"
        data-testid="hero-perks-bar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex gap-3 overflow-x-auto lg:overflow-visible lg:grid lg:grid-cols-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {PERKS.map((perk, i) => (
            <motion.div
              key={perk.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15 + i * 0.08, duration: 0.6 }}
              data-testid={`perk-${perk.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 shrink-0 rounded-2xl px-4 py-3 ${
                perk.hot
                  ? "bg-brand-yellow text-brand-ink shadow-[0_8px_30px_rgba(255,230,0,0.35)]"
                  : "text-white/80"
              }`}
            >
              <perk.icon size={22} strokeWidth={perk.hot ? 2.4 : 1.8} className={perk.hot ? "text-brand-ink" : "text-brand-yellow"} />
              <span className={`text-[13px] leading-tight ${perk.hot ? "font-extrabold" : "font-medium"}`}>
                {perk.label}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
